import { html, useEffect, useState } from '/vendor/htm-preact.module.js';
import { Picture } from '/imageupload.js';
import { get, post, del } from '/api.js';
import { needsPostcode, sharedNames } from '/venues.js';
import { RoleMarks } from '/rolemark.js';

/**
 * The admin panel.
 *
 * Sections render only where the viewer holds authority, so a venue manager
 * opening this sees their venue and nothing else. What may be OFFERED comes
 * from /api/me/capabilities; what is ALLOWED is the server's business. The
 * panel never predicts a refusal — it sends the request and renders the answer,
 * because a client that enforced locally would be a second rulebook to keep in
 * step with the first.
 */

const GLOBAL = 'global';
const ALL = '*';

/**
 * Above this many accounts, the roster waits to be searched — issue #43.
 *
 * Not a page size and not a limit on what you can reach: everything is here,
 * and one keystroke gets at any of it. It is a judgement that past a certain
 * length a list stops being something you read and becomes something you
 * scroll past. Production holds 472 accounts and each row carries up to five
 * buttons, so browsing meant two thousand controls in a column to reach the
 * one person you opened the panel for.
 *
 * A dozen mates is the other common shape of this app, and there the list IS
 * the answer — so below the line nothing changes, and nobody has to type
 * before they can see anybody.
 */
const BROWSE_LIMIT = 25;
// The league administrator role. Global scope, because starting a league is
// not scoped to any league that exists yet — and deliberately NOT `*`, since
// the whole point is a chairman who can run their league without being given
// the keys to the instance.
const LEAGUE_CREATE = 'league.create';

/**
 * Creating a venue, and a way into each one.
 *
 * Running a venue moved to its own page — see venue.js. Making one is a
 * global-admin act and stays here; everything after that is the venue's own
 * business, and a manager holding no site authority should never have to open
 * a screen called Admin to do it.
 */
/**
 * Every league there is — the admin's way of FINDING one.
 *
 * The home screen lists the leagues you are registered to, which is right
 * there and useless here: a league created in error is one nobody joined, so
 * it appears in no list at all. An admin could already open any league by id
 * and delete it; this is the half that was missing, and without it the only
 * person allowed to clean one up was the one person who could not find it.
 *
 * The fixture count is shown because it is what refuses a delete: a league
 * that has been played in is somebody's season.
 */
function Leagues({ isAdmin, onError, onGo }) {
  const [leagues, setLeagues] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    get('/api/admin/leagues').then(setLeagues)
      .catch((e) => onError(e.detail || e.message));
  }, [isAdmin]);

  if (!isAdmin) return null;

  return html`
    <div class="stack" data-testid="admin-leagues">
      <label>Leagues</label>
      ${leagues === null
        ? html`<p class="sub">Loading…</p>`
        : leagues.length === 0
          ? html`<p class="empty" data-testid="admin-leagues-none">
                   None yet.
                 </p>`
          : leagues.map((l) => html`
            <button class="secondary card rowbetween" key=${l.league_id}
                    data-testid=${`admin-league-${l.league_id}`}
                    onClick=${() => onGo('league', l.league_id)}>
              <span style="min-width:0">
                <span style="display:block">${l.name}</span>
                <span class="sub">
                  ${`${l.source === 'external' ? 'mirrored' : 'run here'} · `}
                  ${`${l.fixtures} fixture${l.fixtures === 1 ? '' : 's'} · `}
                  ${`${l.members} member${l.members === 1 ? '' : 's'}`}
                  ${l.mine ? ' · yours' : ''}
                </span>
              </span>
            </button>`)}
    </div>`;
}

function Venues({ isAdmin, onError, onGo }) {
  const [venues, setVenues] = useState([]);
  const [name, setName] = useState('');

  async function reload() { setVenues(await get('/api/venues')); }

  useEffect(() => {
    reload().catch((e) => onError(e.detail || e.message));
  }, []);

  return html`
    <div class="stack" data-testid="admin-venues">
      <label>Venues</label>

      ${isAdmin && html`
        <div class="row pair">
          <input data-testid="venue-name" value=${name} placeholder="Venue name"
                 onInput=${(e) => setName(e.target.value)} />
          <button class="secondary" data-testid="venue-create"
                  onClick=${async () => {
                    try {
                      await post('/api/venues', { name });
                      setName('');
                      await reload();
                    } catch (e) { onError(e.detail || e.message); }
                  }}>Add venue</button>
        </div>`}

      ${(() => { const shared = sharedNames(venues); return venues.map((v) => {
        // Only where the name is ambiguous. A postcode under a pub with no
        // twin is noise on every row, solving a problem those rows have not
        // got; under one of two White Harts it is the whole answer.
        // Absent when the caller may not see it — /api/venues hands the
        // postcode only to somebody who could already reach it another way —
        // so this falls back to the name alone rather than an empty line.
        const sub = [needsPostcode(v, shared) ? v.postcode : '', v.my_bundle]
          .filter(Boolean).join(' · ');
        return html`
        <button key=${v.venue_id} class="secondary card rowbetween"
                data-testid=${`admin-venue-${v.venue_id}`}
                onClick=${() => onGo('venue', v.venue_id)}>
          <span>${v.name}</span>
          <span class="sub">${sub}</span>
        </button>`; }); })()}
    </div>`;
}


/**
 * The message centre — GitHub issue #71.
 *
 * Issue #67 built the envelope and dropped what it collected into a list at
 * the bottom of this screen. That is a drop box, not a channel: nothing said
 * a message had arrived, nothing recorded that anybody had read it, and
 * nothing could take one off the list, so the queue only ever grew.
 *
 * Its own page for the reason the league's roles list got one (#69) and the
 * registered players before that (#46): it is the section here that grows
 * without limit while everything around it is a fixed handful of controls.
 *
 * MARKED READ ON OPENING, by posting rather than by the GET that fetched
 * them. This screen refetches after every write, so a GET that marked things
 * seen would clear the count on whatever happened to poll next — which is
 * not the same event as somebody having looked at it.
 */
function Messages({ support, act, onOpened }) {
  useEffect(() => { onOpened(); }, []);

  return html`
    <div class="stack" data-testid="admin-messages">
      ${support.length === 0
        ? html`<p class="empty" data-testid="admin-support-none">
                 Nobody has asked for help.
               </p>`
        : support.map((m) => html`
            <div key=${m.message_id} class="card stack"
                 data-testid=${`admin-message-${m.message_id}`}>
              <span class="sub">
                ${m.name}${m.seen ? '' : ' · new'}
              </span>
              <span style="text-transform:none">${m.text}</span>
              ${/* Off the QUEUE, not out of the log -- the message stays in
                    the record of what was said. See the command's own
                    docstring: a support queue is the evidence of what went
                    wrong, and tidying it must not destroy that. */''}
              <button class="secondary"
                      data-testid=${`admin-message-delete-${m.message_id}`}
                      onClick=${() => act(() => del(
                        `/api/support/${encodeURIComponent(m.message_id)}`))}>
                Dealt with
              </button>
            </div>`)}
    </div>`;
}

export function AdminScreen({ onDone, onGo, onSwitched }) {
  const [caps, setCaps] = useState(null);
  const [people, setPeople] = useState([]);
  const [error, setError] = useState(null);
  const [recovery, setRecovery] = useState(null);
  const [search, setSearch] = useState('');
  // What people have asked us for. Issue #67.
  const [support, setSupport] = useState([]);
  // Which sub-page is open, or null for the panel itself. Issue #71.
  const [page, setPage] = useState(null);
  // Pictures nobody has vouched for yet. Issue #66.
  const [waiting, setWaiting] = useState([]);

  async function reload() {
    setCaps(await get('/api/me/capabilities'));
    setPeople(await get('/api/people'));
    // Failure-tolerant: the panel's job is people and grants,
    // and a support list that cannot be had must not take the
    // screen down with it.
    setSupport(await get('/api/support').catch(() => []));
    setWaiting(await get('/api/images/pending').catch(() => []));
  }

  useEffect(() => {
    reload().catch((e) => setError(e.detail || e.message));
  }, []);

  if (caps === null) return html`<p class="sub">Loading…</p>`;

  async function act(fn) {
    setError(null);
    try {
      await fn();
      await reload();
    } catch (e) {
      // 422 carries the service's own reason — the last-admin refusal, for
      // one. Show it verbatim beside the control that caused it rather than
      // inventing wording, and never leave a button that appears to do nothing.
      setError(e.detail || e.message);
    }
  }

  const grant = (subject) => act(() => post('/api/grants', {
    subject_id: subject, scope_type: GLOBAL, scope_id: null,
    capability: ALL, cascade: false,
  }));

  const revoke = (subject) => act(() => post('/api/grants/revoke', {
    subject_id: subject, scope_type: GLOBAL, scope_id: null, capability: ALL,
  }));

  const setLeagueRole = (subject, on) => act(() => post(
    on ? '/api/grants' : '/api/grants/revoke', {
      subject_id: subject, scope_type: GLOBAL, scope_id: null,
      capability: LEAGUE_CREATE, ...(on ? { cascade: false } : {}),
    }));

  // Client-side, over the payload already in hand. The same choice the stats
  // board made and for the same reason: /api/people is one small response
  // that is already loaded, so a round trip per keystroke would buy nothing
  // and a server-side index could disagree with what the rows show.
  const query = search.trim().toLowerCase();
  const matching = query
    ? people.filter((p) => (p.name || '').toLowerCase().includes(query))
    : people;
  // Empty query on a long roster shows the prompt, not the wall. On a short
  // one it shows everybody, exactly as it always has.
  const browsing = !query && people.length > BROWSE_LIMIT;
  const shown = browsing ? [] : matching;

  // Counted from the list already in hand rather than asked for separately.
  // `seen` comes back on every row (see `Service.support_messages`), so the
  // number on the way in cannot disagree with what the page behind it draws.
  const unread = support.filter((m) => !m.seen).length;

  // The message centre, on its own page. Issue #71.
  if (page === 'messages') {
    return html`
      <div class="screen">
        <div class="rowbetween">
          <label>Messages</label>
          <button class="secondary" style="width:auto"
                  data-testid="admin-messages-back"
                  onClick=${() => setPage(null)}>Admin</button>
        </div>
        ${error && html`
          <p class="sub" data-testid="admin-error"
             style="color:var(--danger)">${error}</p>`}
        <${Messages} support=${support} act=${act}
                     onOpened=${() => post('/api/support/seen', {})
                       .then(reload).catch(() => {})} />
      </div>`;
  }

  return html`
    <div class="screen">
      <button class="secondary" onClick=${onDone}>Back</button>
      <h2>Admin</h2>

      ${error && html`
        <p class="sub" data-testid="admin-error"
           style="color:var(--danger)">${error}</p>`}

      ${caps.is_admin && html`
        ${/* GitHub issue #67. Newest first, which is how the fold keeps
              them -- somebody reading this is looking for what just went
              wrong, not for the history of it. */''}
        ${/* THE APPROVAL QUEUE -- GitHub issue #66. Nothing an uploader
              sent is ever shown here: what is drawn is what `images.prepare`
              re-encoded, the same bytes everybody else would eventually see.
              An admin judging a picture has to be looking at the picture.

              One per account can be waiting, so this list is bounded by the
              number of PEOPLE rather than by anybody's patience. */''}
        <div class="stack" data-testid="admin-images">
          <label>${`Pictures waiting (${waiting.length})`}</label>
          ${waiting.length === 0
            ? html`<p class="empty" data-testid="admin-images-none">
                     Nothing waiting.
                   </p>`
            : waiting.map((img) => html`
                <div key=${img.image_id} class="card stack"
                     data-testid=${`admin-image-${img.image_id}`}>
                  <${Picture} imageId=${img.image_id} className="venuephoto"
                              alt=${`${img.kind} for ${img.subject}`} />
                  <span class="sub">
                    ${`${img.kind} — ${img.subject} · sent by ${img.by}`}
                  </span>
                  <div class="row pair">
                    <button class="secondary"
                            data-testid=${`admin-image-approve-${img.image_id}`}
                            onClick=${() => act(() => post(
                              `/api/images/${img.image_id}/approve`, {}))}>
                      Let it through
                    </button>
                    <button class="secondary"
                            data-testid=${`admin-image-reject-${img.image_id}`}
                            onClick=${() => act(() => post(
                              `/api/images/${img.image_id}/reject`, {}))}>
                      No
                    </button>
                  </div>
                </div>`)}
        </div>

        ${/* THE WAY IN to the message centre -- issue #71. The list used
              to be drawn right here, growing without limit in the middle
              of a screen of fixed-size controls, with no way to take
              anything off it and nothing to say a message was new. The
              count is the unread ones, not the total: a number that never
              goes down is not worth reading twice. */''}
        <button class="secondary card rowbetween"
                data-testid="admin-go-messages"
                onClick=${() => setPage('messages')}>
          <span>Messages</span>
          <span class="sub" data-testid="admin-messages-unread">
            ${unread === 0 ? 'Nothing new' : `${unread} new`}
          </span>
        </button>

        <div class="stack" data-testid="admin-people">
          <label>People</label>

          <button class="secondary" data-testid="admin-me-demote"
                  onClick=${() => revoke(caps.me)}>
            Step down as admin
          </button>

          ${/* GitHub issue #43. No debounce and no request: the filter runs
                over a list that is already here, so the work per keystroke is
                one pass over a few hundred short strings. */''}
          <input data-testid="admin-people-search" value=${search}
                 placeholder="Search people"
                 onInput=${(e) => setSearch(e.target.value)} />

          ${browsing && html`
            <p class="sub" data-testid="admin-people-browse">
              ${`${people.length} people. Type a name to find one.`}
            </p>`}

          ${query && shown.length === 0 && html`
            <p class="empty" data-testid="admin-people-none">
              Nobody here is called that.
            </p>`}

          ${shown.map((p) => html`
            <div key=${p.user_id} class="card rowbetween"
                 data-testid=${`admin-person-${p.user_id}`}>
              ${/* Issue #59. `only` is unset here: this is the one screen
                    that exists to show authority, so it shows all of it. */''}
              <span><${RoleMarks} roles=${p.roles} />${p.name}</span>
              ${p.is_admin
                ? html`<button class="secondary" data-testid="demote"
                               onClick=${() => revoke(p.user_id)}>
                         Remove admin
                       </button>`
                : html`<button class="secondary" data-testid="promote"
                               onClick=${() => grant(p.user_id)}>
                         Make admin
                       </button>`}
              ${/* A separate role, and it reads as one: they can start a
                    league and nothing else. */''}
              ${p.league_founder
                ? html`<button class="secondary" data-testid="league-role-off"
                               onClick=${() => setLeagueRole(p.user_id, false)}>
                         Cannot start leagues
                       </button>`
                : html`<button class="secondary" data-testid="league-role-on"
                               onClick=${() => setLeagueRole(p.user_id, true)}>
                         Can start leagues
                       </button>`}
              ${/* Reading only — the server refuses every write while it is
                    on, and the banner says so. Offered per person here
                    because "why does Ben see a 404" is a question about one
                    person, and this is the list of them. */''}
              ${p.user_id !== caps.me && html`
                <button class="secondary" data-testid="view-as"
                        onClick=${() => act(async () => {
                          await post('/api/admin/view-as',
                                     { user_id: p.user_id });
                          // Not `onDone`: that only navigates, and the
                          // session now answers as somebody else. This
                          // re-reads /api/auth/state, which is what puts
                          // the banner up.
                          (onSwitched || onDone)();
                        })}>View as</button>`}
              <button class="secondary" data-testid="recovery-invite"
                      onClick=${() => act(async () => {
                        const r = await post('/api/invites',
                                             { bind_user_id: p.user_id });
                        setRecovery(r.code);
                      })}>Recovery code</button>
            </div>`)}
          ${recovery && html`
            <div class="card" data-testid="recovery-code-shown">
              <p class="sub">They redeem this on their new device. It attaches
                 to the account they already have — it does not make a new one.</p>
              <code>${recovery}</code>
            </div>`}
        </div>`}

      <${Venues} isAdmin=${caps.is_admin} onError=${setError} onGo=${onGo} />
      <${Leagues} isAdmin=${caps.is_admin} onError=${setError} onGo=${onGo} />
    </div>`;
}
